import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Workflow, GenerationJob, Asset, Configuration } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import CustomDropdown from '../gallery/CustomDropdown';
import SearchableWorkflowDropdown, { SearchableWorkflowDropdownRef } from './SearchableWorkflowDropdown';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const GeneratePage: React.FC = () => {
  const { user } = useAuth();
  const workflowDropdownRef = useRef<SearchableWorkflowDropdownRef>(null);

  // Left Panel - Workflow Selection
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(null);
  const [savedConfigs, setSavedConfigs] = useState<Configuration[]>([]);

  // Center Panel - Parameters
  const [currentConfig, setCurrentConfig] = useState<any>({});
  const [assets, setAssets] = useState<Asset[]>([]);
  const [jobName, setJobName] = useState('');
  const [batchSize, setBatchSize] = useState(1);
  const [configName, setConfigName] = useState('');

  // Right Panel - Queue & History
  const [recentJobs, setRecentJobs] = useState<GenerationJob[]>([]);

  // Loading states
  const [loadingWorkflows, setLoadingWorkflows] = useState(true);

  // Fetch workflows on mount
  useEffect(() => {
    fetchWorkflows();
    fetchAssets();
  }, []);

  // Auto-refresh recent jobs
  useEffect(() => {
    fetchRecentJobs();
    const interval = setInterval(() => {
      fetchRecentJobs();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Fetch saved configs when workflow changes
  useEffect(() => {
    if (selectedWorkflow) {
      fetchSavedConfigurations(selectedWorkflow.id);
    }
  }, [selectedWorkflow?.id]);

  // Keyboard listener for instant search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input, textarea, or the dropdown is already open
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      // Only handle alphanumeric keys and space
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        workflowDropdownRef.current?.openAndSearch(e.key);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const fetchWorkflows = async () => {
    try {
      setLoadingWorkflows(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/workflows`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setWorkflows(response.data);
    } catch (error) {
      console.error('Failed to fetch workflows:', error);
    } finally {
      setLoadingWorkflows(false);
    }
  };

  const fetchAssets = async () => {
    try {
      const response = await axios.get(`${API_BASE}/assets`);
      setAssets(response.data);
    } catch (error) {
      console.error('Failed to fetch assets:', error);
    }
  };

  const fetchRecentJobs = async () => {
    try {
      const params = new URLSearchParams();
      params.append('page', '1');
      params.append('limit', '5');

      const response = await axios.get(`${API_BASE}/jobs?${params}`);
      setRecentJobs(response.data.jobs || []);
    } catch (error) {
      console.error('Failed to fetch recent jobs:', error);
    }
  };

  const fetchSavedConfigurations = async (workflowId: number) => {
    try {
      const response = await axios.get(`${API_BASE}/workflows/${workflowId}/configurations`);
      setSavedConfigs(response.data);
    } catch (error) {
      console.error('Failed to fetch configurations:', error);
    }
  };

  const selectWorkflow = async (workflow: Workflow) => {
    setSelectedWorkflow(workflow);

    // Fetch full workflow details
    try {
      const response = await axios.get(`${API_BASE}/workflows/${workflow.id}`);
      const workflowData = response.data;

      // Initialize default config based on workflow type
      if (workflowData.workflow_type === 'slug') {
        const defaultConfig: any = {};
        (workflowData.workflow_overrides || []).forEach((override: any) => {
          if (override.default_value !== null && override.default_value !== undefined) {
            defaultConfig[override.override_key!] = override.default_value;
          }
        });
        setCurrentConfig(defaultConfig);
        setSelectedWorkflow(workflowData);
      } else {
        const defaultConfig: any = {};
        Object.entries(workflowData.configurable_params || {}).forEach(([key, config]: [string, any]) => {
          if (config.editable) {
            if (config.type === 'seed_control') {
              defaultConfig[`${key}_mode`] = config.default_mode || 'random';
              defaultConfig[`${key}_value`] = config.default_value || Math.floor(Math.random() * 0xFFFFFFFF);
            } else {
              defaultConfig[key] = config.default_value || '';
            }
          }
        });
        setCurrentConfig(defaultConfig);
        setSelectedWorkflow(workflowData);
      }
    } catch (error) {
      console.error('Failed to fetch workflow details:', error);
    }
  };

  const toggleFavorite = async (workflowId: number, event?: React.MouseEvent) => {
    if (event) event.stopPropagation();

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE}/workflows/${workflowId}/favorite`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setWorkflows(workflows.map(w =>
        w.id === workflowId ? { ...w, is_favorite: response.data.is_favorite } : w
      ));

      if (selectedWorkflow?.id === workflowId) {
        setSelectedWorkflow({ ...selectedWorkflow, is_favorite: response.data.is_favorite });
      }

      // Refresh workflows to update favorites list
      fetchWorkflows();
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };


  const updateParameter = (key: string, value: any) => {
    setCurrentConfig({ ...currentConfig, [key]: value });
  };

  const loadConfiguration = (config: Configuration) => {
    setCurrentConfig(config.parameter_values);
  };

  const saveConfiguration = async () => {
    if (!configName.trim()) {
      alert('Please enter a configuration name');
      return;
    }

    if (!selectedWorkflow) {
      alert('Please select a workflow first');
      return;
    }

    try {
      await axios.post(`${API_BASE}/configurations`, {
        workflow_id: selectedWorkflow.id,
        config_name: configName,
        parameter_values: currentConfig
      });

      setConfigName('');
      fetchSavedConfigurations(selectedWorkflow.id);
      alert('Configuration saved successfully!');
    } catch (error) {
      console.error('Failed to save configuration:', error);
      alert('Failed to save configuration');
    }
  };

  const submitJob = async () => {
    if (!selectedWorkflow) {
      alert('Please select a workflow first');
      return;
    }

    if (batchSize < 1 || batchSize > 999999) {
      alert('Batch size must be between 1 and 999999');
      return;
    }

    // Validate required fields for slug workflows
    if (selectedWorkflow.workflow_type === 'slug') {
      const overrides = selectedWorkflow.workflow_overrides || [];
      for (const override of overrides) {
        if (override.is_required && override.override_key) {
          const value = currentConfig[override.override_key];
          if (value === null || value === undefined || value === '') {
            alert(`${override.display_name} is required`);
            return;
          }
        }
      }
    }

    try {
      const response = await axios.post(`${API_BASE}/jobs`, {
        workflow_id: selectedWorkflow.id,
        job_name: jobName,
        parameters: currentConfig,
        batch_size: batchSize
      });

      alert(`Job submitted successfully! ${batchSize > 1 ? `Generating ${batchSize} images` : 'Generating 1 image'}`);
      setJobName('');
      setBatchSize(1);

      // Refresh history
      fetchRecentJobs();
    } catch (error) {
      console.error('Failed to submit job:', error);
      alert('Failed to submit job');
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Image Generation': return '🎨';
      case 'Text Processing': return '📝';
      case 'Video Generation': return '🎬';
      case 'Audio Processing': return '🎵';
      default: return '⚙️';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600';
      case 'processing': return 'text-orange-600';
      case 'queued': return 'text-blue-600';
      case 'failed': return 'text-red-600';
      case 'cancelled': return 'text-gray-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return '✅';
      case 'processing': return '🔄';
      case 'queued': return '⏳';
      case 'failed': return '❌';
      case 'cancelled': return '🚫';
      default: return '❓';
    }
  };

  const renderParameterInput = (key: string, config: any) => {
    if (!config.editable) return null;

    const value = currentConfig[key] || config.default_value || '';

    switch (config.type) {
      case 'seed_control':
        const seedMode = currentConfig[`${key}_mode`] || config.default_mode || 'random';
        const seedValue = currentConfig[`${key}_value`] || config.default_value || 0;

        const seedModeOptions = [
          { value: 'random', label: 'Random', icon: '🎲' },
          { value: 'fixed', label: 'Fixed', icon: '🔢' }
        ];

        return (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-1">
                <div className="h-[40px] [&>button]:!h-[40px] [&>button]:!py-2">
                  <CustomDropdown
                    options={seedModeOptions}
                    value={seedMode}
                    onChange={(value) => {
                      if (value === 'random') {
                        setCurrentConfig({
                          ...currentConfig,
                          [`${key}_mode`]: value,
                          [`${key}_value`]: Math.floor(Math.random() * 0xFFFFFFFF)
                        });
                      } else {
                        updateParameter(`${key}_mode`, value);
                      }
                    }}
                  />
                </div>
              </div>

              {seedMode === 'fixed' && (
                <div className="col-span-2">
                  <input
                    type="number"
                    className="w-full h-[40px] px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    value={seedValue}
                    min="0"
                    max="4294967295"
                    onChange={(e) => updateParameter(`${key}_value`, parseInt(e.target.value) || 0)}
                    placeholder="Enter seed (0-4294967295)"
                  />
                </div>
              )}

              {seedMode === 'random' && (
                <div className="col-span-2 h-[40px] px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-600 text-sm flex items-center justify-between">
                  <span>Current: {seedValue}</span>
                  <button
                    type="button"
                    className="px-2 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                    onClick={() => updateParameter(`${key}_value`, Math.floor(Math.random() * 0xFFFFFFFF))}
                  >
                    Regenerate
                  </button>
                </div>
              )}
            </div>
            {batchSize > 1 && (
              <div className="text-xs text-blue-600 bg-blue-50 px-3 py-2 rounded-lg">
                <span className="font-medium">Batch Mode:</span> Each of the {batchSize} generations will use a unique random seed
              </div>
            )}
          </div>
        );

      case 'number':
        return (
          <input
            type="number"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={value}
            min={config.min}
            max={config.max}
            step={config.step || 1}
            onChange={(e) => updateParameter(key, parseFloat(e.target.value))}
          />
        );
      case 'select':
        return (
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={value}
            onChange={(e) => updateParameter(key, e.target.value)}
          >
            {config.options?.map((option: string) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );
      case 'asset':
        const filteredAssets = assets.filter(asset => asset.type === config.asset_type);
        return (
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={value}
            onChange={(e) => updateParameter(key, e.target.value)}
          >
            <option value="">Select {config.asset_type}...</option>
            {filteredAssets.map((asset) => (
              <option key={asset.id} value={asset.filename}>
                {asset.display_name}
              </option>
            ))}
          </select>
        );
      default:
        return (
          <input
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={value}
            onChange={(e) => updateParameter(key, e.target.value)}
          />
        );
    }
  };

  const renderSlugParameterInput = (override: any) => {
    const paramKey = override.override_key!;
    const value = currentConfig[paramKey] || '';

    switch (override.data_type) {
      case 'integer':
        const intRules = override.validation_rules || {};
        return (
          <input
            type="number"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={value}
            min={intRules.min}
            max={intRules.max}
            step={1}
            onChange={(e) => updateParameter(paramKey, parseInt(e.target.value) || 0)}
          />
        );

      case 'float':
        const floatRules = override.validation_rules || {};
        return (
          <input
            type="number"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={value}
            min={floatRules.min}
            max={floatRules.max}
            step={floatRules.step || 0.1}
            onChange={(e) => updateParameter(paramKey, parseFloat(e.target.value) || 0)}
          />
        );

      case 'boolean':
        return (
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => updateParameter(paramKey, e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label className="ml-2 text-sm text-gray-700">
              {value ? 'Enabled' : 'Disabled'}
            </label>
          </div>
        );

      case 'select':
        const options = override.validation_rules?.options || [];
        return (
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={value}
            onChange={(e) => updateParameter(paramKey, e.target.value)}
          >
            <option value="">Select option...</option>
            {options.map((option: string) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );

      case 'string':
      default:
        const stringRules = override.validation_rules || {};
        return (
          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={value}
            rows={3}
            maxLength={stringRules.max_length}
            onChange={(e) => updateParameter(paramKey, e.target.value)}
            placeholder={override.description || ''}
          />
        );
    }
  };

  // Get favorite workflows (always separate from search)
  const favoriteWorkflows = workflows.filter(w => w.is_favorite);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Generate</h1>
        <div className="text-sm text-gray-500">
          All-in-one workflow execution dashboard
        </div>
      </div>

      {/* Three-Panel Layout */}
      <div className="grid grid-cols-12 gap-6 flex-1 min-h-0">

        {/* LEFT PANEL - Compact Workflow Selection */}
        <div className="col-span-3 h-full flex flex-col space-y-4 overflow-y-auto pr-2">

          {/* Favorites Section - Always Visible */}
          {favoriteWorkflows.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-3">
              <h3 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                ⭐ Favorites
              </h3>
              <div className="space-y-1">
                {favoriteWorkflows.map((workflow) => (
                  <button
                    key={workflow.id}
                    onClick={() => selectWorkflow(workflow)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-all text-sm ${
                      selectedWorkflow?.id === workflow.id
                        ? 'bg-blue-100 border-2 border-blue-500 text-blue-900 font-medium'
                        : 'bg-gray-50 hover:bg-blue-50 border-2 border-transparent hover:border-blue-200'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm">{getCategoryIcon(workflow.category)}</span>
                      <span className="flex-1 truncate">{workflow.name}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Workflow Selection */}
          <div className="bg-white border border-gray-200 rounded-xl p-3">
            <h3 className="text-xs font-semibold text-gray-700 mb-2">Select Workflow</h3>

            {/* Custom Searchable Workflow Dropdown */}
            <SearchableWorkflowDropdown
              ref={workflowDropdownRef}
              workflows={workflows}
              selectedWorkflow={selectedWorkflow}
              onSelect={selectWorkflow}
              onToggleFavorite={toggleFavorite}
              getCategoryIcon={getCategoryIcon}
              placeholder="Choose a workflow..."
            />
          </div>

          {/* Saved Configs for Selected Workflow */}
          {selectedWorkflow && (
            <div className="bg-white border border-gray-200 rounded-xl p-3 flex-1">
              <h3 className="text-xs font-semibold text-gray-700 mb-2">💾 Saved Configs</h3>
              {savedConfigs.length === 0 ? (
                <div className="text-xs text-gray-500 text-center py-4">
                  No saved configurations
                </div>
              ) : (
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {savedConfigs.map((config) => (
                    <button
                      key={config.id}
                      onClick={() => loadConfiguration(config)}
                      className="w-full text-left bg-gray-50 hover:bg-blue-50 border border-gray-200 hover:border-blue-300 rounded-lg p-2 transition-colors"
                    >
                      <div className="text-sm font-medium text-gray-900">{config.config_name}</div>
                      <div className="text-xs text-gray-500">
                        {new Date(config.created_at).toLocaleDateString()}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* CENTER PANEL - Parameters & Sticky Submit */}
        <div className="col-span-5 h-full flex flex-col">
          {!selectedWorkflow ? (
            <div className="flex-1 flex items-center justify-center bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl">
              <div className="text-center text-gray-500">
                <p className="text-lg mb-2">👈 Select a workflow to begin</p>
                <p className="text-sm">Choose from favorites or the dropdown on the left</p>
              </div>
            </div>
          ) : (
            <>
              {/* Scrollable Parameters Section */}
              <div className="flex-1 overflow-y-auto pr-2 mb-4">
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">⚙️ Parameters</h3>
                  <div className="space-y-3">
                    {selectedWorkflow.workflow_type === 'slug' ? (
                      // Slug workflow parameters
                      (selectedWorkflow.workflow_overrides || []).length === 0 ? (
                        <div className="text-center text-gray-500 py-8 text-sm">
                          No parameters configured for this workflow
                        </div>
                      ) : (
                        (selectedWorkflow.workflow_overrides || []).map((override: any) => (
                          <div key={override.id} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              {override.display_name}
                              {override.is_required && <span className="text-red-500 ml-1">*</span>}
                            </label>
                            {override.description && (
                              <p className="text-xs text-gray-500 mb-2">{override.description}</p>
                            )}
                            {renderSlugParameterInput(override)}
                          </div>
                        ))
                      )
                    ) : (
                      // JSON workflow parameters
                      Object.entries(selectedWorkflow.configurable_params || {}).filter(([, config]: [string, any]) => config.editable).length === 0 ? (
                        <div className="text-center text-gray-500 py-8 text-sm">
                          No editable parameters for this workflow
                        </div>
                      ) : (
                        Object.entries(selectedWorkflow.configurable_params || {}).map(([key, config]: [string, any]) => (
                          config.editable && (
                            <div key={key} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                {config.display_name || key}
                              </label>
                              {config.description && (
                                <p className="text-xs text-gray-500 mb-2">{config.description}</p>
                              )}
                              {renderParameterInput(key, config)}
                            </div>
                          )
                        ))
                      )
                    )}
                  </div>
                </div>
              </div>

              {/* Sticky Submit Panel */}
              <div className="sticky bottom-0 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-4 shadow-lg">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">🚀 Generate</h3>

                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Job Name (optional)</label>
                    <input
                      type="text"
                      placeholder="Auto-generated if empty"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                      value={jobName}
                      onChange={(e) => setJobName(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Batch Size</label>
                    <input
                      type="number"
                      min="1"
                      max="999999"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                      value={batchSize}
                      onChange={(e) => setBatchSize(Math.max(1, Math.min(999999, parseInt(e.target.value) || 1)))}
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Save Config As (optional)</label>
                  <input
                    type="text"
                    placeholder="e.g., Portrait Setup"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    value={configName}
                    onChange={(e) => setConfigName(e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={submitJob}
                    className="bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold py-2.5 px-4 rounded-lg hover:from-green-600 hover:to-green-700 transition-all shadow-md hover:shadow-lg"
                  >
                    {batchSize > 1 ? `Generate ${batchSize}` : 'Generate'}
                  </button>
                  <button
                    onClick={saveConfiguration}
                    disabled={!configName.trim()}
                    className="bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold py-2.5 px-4 rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Save Config
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* RIGHT PANEL - Queue & History */}
        <div className="col-span-4 h-full flex flex-col space-y-4 overflow-y-auto pr-2">

          {/* Recent History */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">🕐 Recent Jobs</h3>
            <div className="space-y-2">
              {recentJobs.length === 0 ? (
                <div className="text-center text-gray-500 py-4 text-sm">No recent jobs</div>
              ) : (
                recentJobs.map((job) => (
                  <div
                    key={job.id}
                    className="bg-gray-50 border border-gray-200 rounded-lg p-3 transition-all"
                  >
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{getStatusIcon(job.status)}</span>
                        <span className="text-sm font-medium text-gray-900">{job.job_name}</span>
                      </div>
                      <span className={`text-xs font-medium ${getStatusColor(job.status)}`}>
                        {job.status}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600 mb-2">{job.workflow_name}</div>
                    <div className="text-xs text-gray-500 mb-2">
                      {new Date(job.created_at).toLocaleString()}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => window.location.href = `/job/${job.id}`}
                        className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium py-1.5 px-3 rounded transition-colors"
                      >
                        View Details
                      </button>
                      <button
                        onClick={() => window.location.href = `/gallery/${job.id}`}
                        className="flex-1 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium py-1.5 px-3 rounded transition-colors"
                      >
                        View Gallery
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
            <a
              href="/history"
              className="block mt-3 text-center text-xs text-blue-600 hover:text-blue-800 font-medium"
            >
              View Full History →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneratePage;
